import { z } from 'zod';

import type { ChargingStop } from '#domain/ChargingStop.ts';
import type { Location } from '#domain/Location.ts';
import { mapCountryCodeISO3ToISO2 } from '#utils/country.util.ts';

export const hereStationAddressSchema = z.object({
    street: z.string().optional(),
    houseNumber: z.string().optional(),
    city: z.string().optional(),
    postalCode: z.string().optional(),
    countryCode: z.string().optional(),
});

export const mapStationAddress = (address?: z.infer<typeof hereStationAddressSchema> | null): Location | undefined => {
    if (!address) {
        return undefined;
    }
    const location = {
        street: [address.street, address.houseNumber].filter(Boolean).join(' ').trim(),
        city: address.city?.trim() ?? '',
        zipCode: address.postalCode?.trim() ?? '',
        country: mapCountryCodeISO3ToISO2(address.countryCode) ?? '',
    };
    // Incomplete postal data would make legacy address editors reject the stop after charging is cleared.
    return Object.values(location).every(value => value.length > 0 && value.length <= 255) ? location : undefined;
};

const responseSchema = z.object({
    items: z.array(
        z.object({
            address: hereStationAddressSchema,
            distance: z.number().finite().nonnegative(),
        })
    ),
});

// Nearby addresses aid display only; never replace the charging station's routing coordinates.
export const enrichChargingStationAddresses = async (
    stops: ChargingStop[],
    apiKey: string,
    signal: AbortSignal
): Promise<ChargingStop[]> => {
    if (!stops.length || !apiKey || signal.aborted) {
        return stops;
    }
    const lookupSignal = AbortSignal.any([signal, AbortSignal.timeout(5000)]);
    const locations = new Map<string, Location | undefined>();
    const key = (stop: ChargingStop) => `${stop.position.lat},${stop.position.lng}`;
    const missing = [
        ...new Map(
            stops
                .filter(
                    stop =>
                        !stop.location?.street ||
                        !stop.location.city ||
                        !stop.location.country ||
                        !stop.location.zipCode
                )
                .map(stop => [key(stop), stop])
        ).values(),
    ];
    let next = 0;
    await Promise.all(
        Array.from({ length: Math.min(4, missing.length) }, async () => {
            while (next < missing.length && !lookupSignal.aborted) {
                const stop = missing[next++];
                const at = key(stop);
                try {
                    const query = new URLSearchParams({ at, limit: '1', apiKey });
                    const response = await fetch(`https://revgeocode.search.hereapi.com/v1/revgeocode?${query}`, {
                        signal: lookupSignal,
                    });
                    if (!response.ok) {
                        continue;
                    }
                    const parsed = responseSchema.safeParse(await response.json());
                    const item = parsed.success ? parsed.data.items[0] : undefined;
                    // Do not present an unrelated distant address as the station's address.
                    if (item && item.distance <= 100) {
                        locations.set(at, mapStationAddress(item.address));
                    }
                } catch {
                    // Missing entitlement, timeout and network errors retain coordinate-only stops.
                }
            }
        })
    );
    if (signal.aborted) {
        return stops;
    }
    return stops.map(stop => {
        const found = locations.get(key(stop));
        if (!found) {
            return stop;
        }
        return {
            ...stop,
            location: {
                street: stop.location?.street || found.street,
                city: stop.location?.city || found.city,
                zipCode: stop.location?.zipCode || found.zipCode,
                country: stop.location?.country || found.country,
            },
        };
    });
};
